import { prefersReducedMotion } from './timing'

/** A little air above anything aligned to the top of the frame. */
const MARGIN = 24

/** Bounds on how long the travel takes, whatever the distance. */
const MIN_MS = 260
const MAX_MS = 620
/** Milliseconds per pixel between those bounds. */
const MS_PER_PX = 0.7

/**
 * How far the page may drift from where we are putting it before we conclude the
 * guest has taken over. Generous enough to absorb sub-pixel rounding.
 */
const HIJACK_TOLERANCE_PX = 4

/**
 * Where to put the element.
 *
 * - `center` for something small, so it sits in the middle of the frame.
 * - `start` for something long the guest is about to work through top-down.
 * - `nearest` for the smallest move that brings the whole of it into view,
 *   leaving the page alone if it is already there.
 *
 * Anything too tall to show at once is aligned to its top whichever is asked
 * for: centring it would push its heading off the screen.
 */
export type Align = 'start' | 'center' | 'nearest'

/**
 * The scroll offset that would put `element` where `align` asks.
 *
 * Worked out here rather than left to `scrollIntoView` so callers can know
 * whether a scroll is needed at all - `scrollend` never fires for a scroll that
 * does not happen, and waiting on it would hang.
 */
function targetScrollTop(element: HTMLElement, align: Align): number {
  const box = element.getBoundingClientRect()
  const top = box.top + window.scrollY
  const bottom = top + box.height
  const viewport = window.innerHeight
  const furthest = Math.max(0, document.documentElement.scrollHeight - viewport)

  let wanted: number
  if (box.height + MARGIN * 2 > viewport) {
    wanted = top - MARGIN
  } else if (align === 'center') {
    wanted = top - (viewport - box.height) / 2
  } else if (align === 'start') {
    wanted = top - MARGIN
  } else if (top - MARGIN < window.scrollY) {
    // Above the frame: bring its top down into view.
    wanted = top - MARGIN
  } else if (bottom + MARGIN > window.scrollY + viewport) {
    // Below the frame: bring its bottom up into view.
    wanted = bottom + MARGIN - viewport
  } else {
    wanted = window.scrollY
  }

  return Math.min(Math.max(wanted, 0), furthest)
}

/** Ease-out cubic: quick off the mark, gentle into place. */
function ease(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

/**
 * Travels the page to `target`, resolving when it arrives.
 *
 * Driven a frame at a time rather than handed to `behavior: 'smooth'`, which
 * sounds like more work than it is and buys two things we need.
 *
 * The first is that it actually happens. A native smooth scroll requested from
 * the submit handler was being silently discarded - measured: the call went in
 * with `{top: 182, behavior: 'smooth'}` and the page sat at 735 for the next
 * 1300ms without moving a pixel, while the identical call a moment earlier
 * worked. Something in the render that follows the click cancels it, and the
 * browser gives no signal that it has.
 *
 * The second is a completion signal we can trust. `scrollend` is not in every
 * browser we care about, and it never fires at all for a scroll that gets
 * cancelled - so waiting on it meant waiting on a timeout, and starting the
 * send-off from a page that had not moved.
 *
 * Each frame is an instantaneous scroll, so there is no native animation to
 * cancel. If the page ends up somewhere we did not put it, the guest has taken
 * over and we stop rather than fight them for control.
 */
function travelTo(target: number): Promise<void> {
  const from = window.scrollY
  const distance = target - from
  if (Math.abs(distance) < 2) return Promise.resolve()

  if (prefersReducedMotion()) {
    window.scrollTo({ top: target, behavior: 'instant' })
    return Promise.resolve()
  }

  const duration = Math.min(MAX_MS, Math.max(MIN_MS, Math.abs(distance) * MS_PER_PX))
  const start = performance.now()
  let expected = from

  return new Promise((resolve) => {
    const step = (now: number) => {
      if (Math.abs(window.scrollY - expected) > HIJACK_TOLERANCE_PX) {
        resolve()
        return
      }
      const t = Math.min(1, (now - start) / duration)
      expected = from + distance * ease(t)
      window.scrollTo({ top: expected, behavior: 'instant' })
      if (t < 1) requestAnimationFrame(step)
      else resolve()
    }
    requestAnimationFrame(step)
  })
}

/**
 * Eases an element into the frame after the page has changed shape around it.
 *
 * Used where a section swaps between two very different heights - the room
 * picker is four cards, its confirmation is a few lines - which moves several
 * hundred pixels of page under the guest while their scroll offset stays put.
 *
 * Deliberately unconditional about firing. An earlier version only scrolled when
 * the target was off screen, which looked erratic in exactly the case that
 * matters: with more than one guest's form open, how far the page moves depends
 * on which one was submitted and what sits above it, so the correction happened
 * on some submissions and not others.
 */
export function scrollIntoFrame(element: HTMLElement, align: Align = 'center'): void {
  void travelTo(targetScrollTop(element, align))
}

/**
 * The same, but resolving once the page has come to rest.
 *
 * For animations that measure where something is before they start. The RSVP
 * send-off takes the card's viewport rect once and positions the dog against
 * that snapshot for its whole three and a half seconds, so it has to be given a
 * page that has stopped moving - otherwise he runs his route against where the
 * card used to be, which off screen means not being seen at all.
 */
export function scrollIntoFrameThenSettle(
  element: HTMLElement,
  align: Align = 'center',
): Promise<void> {
  return travelTo(targetScrollTop(element, align))
}
